
import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Student, SchoolSettings, Teacher } from '../types';
import { Search, UserPlus, X, Check, Trash, Edit2, FileUp, Download, Trash2, AlertCircle, HelpCircle, AlertTriangle, FileSpreadsheet, Filter, ChevronDown, ChevronLeft, ChevronRight, Crown, ShieldCheck, FileText, Wallet, Users, Plus, Shuffle, Copy, RefreshCw, Sparkles, CheckCircle2, GraduationCap, UserCheck, Shield, Award, StickyNote, MessageSquare } from 'lucide-react';
import * as XLSX from 'xlsx';

interface Props {
  students: Student[];
  onUpdate: (students: Student[]) => void;
  availableClasses: string[];
  schoolSettings: SchoolSettings;
  onStudentClick?: (student: Student) => void;
  teachers?: Teacher[];
  onUpdateTeachers?: (teachers: Teacher[]) => void;
  onUpdateTeacher?: (teacher: Teacher) => void;
  userRole?: string;
  activeUser?: Teacher;
}

const StudentsView: React.FC<Props> = ({ 
  students, 
  onUpdate, 
  availableClasses, 
  schoolSettings, 
  onStudentClick,
  teachers = [],
  onUpdateTeachers,
  onUpdateTeacher,
  userRole,
  activeUser
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClass, setSelectedClass] = useState<string>('9A');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showStructure, setShowStructure] = useState(true);
  const [showSekretaris2, setShowSekretaris2] = useState(false);
  const [showBendahara2, setShowBendahara2] = useState(false);
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // State untuk Konfirmasi Hapus
  const [confirmDelete, setConfirmDelete] = useState<{ isOpen: boolean; id: string | 'ALL'; name: string }>({
    isOpen: false,
    id: '',
    name: ''
  });

  // State untuk Modal Pembagian Kelompok Belajar
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [groupClass, setGroupClass] = useState<string>('9A');
  const [groupCount, setGroupCount] = useState<number>(4);
  const [groupSeed, setGroupSeed] = useState<number>(0);
  const [copiedToast, setCopiedToast] = useState(false);

  // State untuk Input Form Nama Wali Kelas Langsung
  const [walasNameInput, setWalasNameInput] = useState('');
  const [walasNipInput, setWalasNipInput] = useState('');

  // State untuk Modal Catatan Guru
  const [noteModalStudent, setNoteModalStudent] = useState<Student | null>(null);
  const [noteInput, setNoteInput] = useState<string>('');
  const [noteSavedToast, setNoteSavedToast] = useState(false);

  const handleOpenNoteModal = (e: React.MouseEvent, student: Student) => {
    e.preventDefault();
    e.stopPropagation();
    setNoteModalStudent(student);
    setNoteInput(student.notes || student.specialNotes || student.classRoleNote || '');
  };

  const handleSaveNote = () => {
    if (!noteModalStudent) return;
    const trimmed = noteInput.trim();
    const updatedList = (students || []).map(s => {
      if (s.id === noteModalStudent.id) {
        return { 
          ...s, 
          notes: trimmed || undefined, 
          specialNotes: trimmed || undefined 
        };
      }
      return s;
    });
    onUpdate(updatedList);
    setNoteSavedToast(true);
    setTimeout(() => setNoteSavedToast(false), 2500);
    setNoteModalStudent(null);
  };

  const isFemaleStudent = (s: Student) => {
    const g = (s.gender || '').trim().toLowerCase();
    return g === 'perempuan' || g === 'p' || g === 'female' || g === 'f';
  };

  const groupStudents = useMemo(() => {
    if (!groupClass) return [];
    if (groupClass === 'SEMUA') {
      return (students || []).filter(s => s.status === 'Aktif' || !s.status);
    }
    return (students || []).filter(s => s.class === groupClass && (s.status === 'Aktif' || !s.status));
  }, [students, groupClass]);

  const maleStudents = useMemo(() => {
    return groupStudents.filter(s => !isFemaleStudent(s));
  }, [groupStudents]);

  const femaleStudents = useMemo(() => {
    return groupStudents.filter(s => isFemaleStudent(s));
  }, [groupStudents]);

  const generatedGroups = useMemo(() => {
    if (groupStudents.length === 0 || groupCount <= 0) return [];

    const shuffleArray = <T,>(array: T[]): T[] => {
      const arr = [...array];
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
      return arr;
    };

    const shuffledMales = shuffleArray(maleStudents);
    const shuffledFemales = shuffleArray(femaleStudents);

    const numGroups = Math.max(1, Math.min(groupCount, groupStudents.length));
    const groups: {
      id: number;
      name: string;
      members: Student[];
      males: Student[];
      females: Student[];
    }[] = Array.from({ length: numGroups }, (_, i) => ({
      id: i + 1,
      name: `Kelompok ${i + 1}`,
      members: [],
      males: [],
      females: []
    }));

    // Distribute males evenly across groups
    shuffledMales.forEach((m, idx) => {
      const targetIdx = idx % numGroups;
      groups[targetIdx].members.push(m);
      groups[targetIdx].males.push(m);
    });

    // Distribute females starting with offset to balance total numbers
    const femaleOffset = shuffledMales.length % numGroups;
    shuffledFemales.forEach((f, idx) => {
      const targetIdx = (idx + femaleOffset) % numGroups;
      groups[targetIdx].members.push(f);
      groups[targetIdx].females.push(f);
    });

    return groups;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupStudents, maleStudents, femaleStudents, groupCount, groupSeed]);

  const handleCopyGroupText = () => {
    if (generatedGroups.length === 0) return;
    let text = `*PEMBAGIAN KELOMPOK BELAJAR (ADIL GENDER)*\n`;
    text += `Kelas: ${groupClass === 'SEMUA' ? 'Semua Kelas' : `Kelas ${groupClass}`}\n`;
    text += `Total: ${groupStudents.length} Siswa (${maleStudents.length} Laki-laki, ${femaleStudents.length} Perempuan)\n`;
    text += `Jumlah Kelompok: ${generatedGroups.length}\n`;
    text += `------------------------------------\n\n`;

    generatedGroups.forEach(g => {
      text += `*${g.name.toUpperCase()}* (${g.members.length} Siswa: ${g.males.length}L, ${g.females.length}P)\n`;
      g.members.forEach((m, idx) => {
        const gTag = isFemaleStudent(m) ? '(P)' : '(L)';
        text += `${idx + 1}. ${m.name} ${gTag}\n`;
      });
      text += `\n`;
    });

    navigator.clipboard.writeText(text);
    setCopiedToast(true);
    setTimeout(() => setCopiedToast(false), 2500);
  };

  const handleExportGroupExcel = () => {
    if (generatedGroups.length === 0) return;
    const rows: any[] = [];
    rows.push(['PEMBAGIAN KELOMPOK BELAJAR ADIL GENDER']);
    rows.push(['Kelas:', groupClass]);
    rows.push(['Total Siswa:', groupStudents.length, `(${maleStudents.length} L, ${femaleStudents.length} P)`]);
    rows.push([]);

    generatedGroups.forEach(g => {
      rows.push([g.name.toUpperCase(), `(${g.members.length} Siswa: ${g.males.length} Laki-laki, ${g.females.length} Perempuan)`]);
      rows.push(['No', 'Nama Siswa', 'Jenis Kelamin', 'NISN']);
      g.members.forEach((m, idx) => {
        rows.push([
          idx + 1,
          m.name,
          m.gender === 'Perempuan' ? 'P' : 'L',
          m.nisn || m.nis || '-'
        ]);
      });
      rows.push([]);
    });

    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 6 }, { wch: 30 }, { wch: 15 }, { wch: 18 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Kelompok_Belajar");
    XLSX.writeFile(wb, `Kelompok_Belajar_Kelas_${groupClass}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const [formData, setFormData] = useState<Partial<Student>>({
    name: '', nis: '', nisn: '', class: '', gender: 'Laki-laki', status: 'Aktif', classRole: ''
  });

  const sortedClasses = useMemo(() => {
    return [...availableClasses].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
  }, [availableClasses]);

  const classList = useMemo(() => ['SEMUA', ...sortedClasses], [sortedClasses]);

  // Sync selectedClass and groupClass if the current selected class is not in sortedClasses
  useEffect(() => {
    if (sortedClasses.length > 0) {
      if (selectedClass !== 'SEMUA' && !sortedClasses.includes(selectedClass)) {
        setSelectedClass(sortedClasses[0] || 'SEMUA');
      }
      if (!sortedClasses.includes(groupClass)) {
        setGroupClass(sortedClasses[0] || '');
      }
    }
  }, [sortedClasses, selectedClass, groupClass]);

  const handlePrevClass = () => {
    if (classList.length <= 1) return;
    const currentIndex = classList.indexOf(selectedClass);
    if (currentIndex === -1 || currentIndex === 0) {
      setSelectedClass(classList[classList.length - 1]);
    } else {
      setSelectedClass(classList[currentIndex - 1]);
    }
  };

  const handleNextClass = () => {
    if (classList.length <= 1) return;
    const currentIndex = classList.indexOf(selectedClass);
    if (currentIndex === -1 || currentIndex === classList.length - 1) {
      setSelectedClass(classList[0]);
    } else {
      setSelectedClass(classList[currentIndex + 1]);
    }
  };

  const activeClass = selectedClass === 'SEMUA' ? (sortedClasses[0] || '') : selectedClass;

  const currentHomeroomTeacher = useMemo(() => {
    if (!activeClass || !teachers) return null;
    return teachers.find(t => t.homeroomClass === activeClass) || null;
  }, [teachers, activeClass]);

  // Sync direct input fields when currentHomeroomTeacher or activeClass changes
  useEffect(() => {
    if (currentHomeroomTeacher) {
      setWalasNameInput(currentHomeroomTeacher.name || '');
      setWalasNipInput(currentHomeroomTeacher.nip || '');
    } else {
      setWalasNameInput('');
      setWalasNipInput('');
    }
  }, [currentHomeroomTeacher, activeClass]);

  const handleSaveHomeroomTeacherName = (name: string, nip?: string) => {
    if (!activeClass) return;
    const trimmedName = name.trim();
    if (!trimmedName) {
      handleClearHomeroomTeacher();
      return;
    }

    const existingTeacherIndex = (teachers || []).findIndex(t => t.homeroomClass === activeClass);
    let updatedTeachers: Teacher[];

    if (existingTeacherIndex >= 0) {
      updatedTeachers = teachers.map((t, idx) => {
        if (idx === existingTeacherIndex) {
          return { ...t, name: trimmedName, nip: nip !== undefined ? nip.trim() : t.nip };
        }
        return t;
      });
    } else {
      const matchNameIndex = (teachers || []).findIndex(t => t.name.toLowerCase() === trimmedName.toLowerCase());
      if (matchNameIndex >= 0) {
        updatedTeachers = teachers.map((t, idx) => {
          if (idx === matchNameIndex) {
            return { ...t, homeroomClass: activeClass, nip: nip !== undefined ? nip.trim() : t.nip };
          }
          return t;
        });
      } else {
        const newTeacher: Teacher = {
          id: `walas-${activeClass.toLowerCase().replace(/[^a-z0-9]/g, '')}-${Date.now()}`,
          name: trimmedName,
          nip: nip ? nip.trim() : '',
          username: `walas_${activeClass.toLowerCase().replace(/[^a-z0-9]/g, '')}`,
          password: 'password123',
          homeroomClass: activeClass,
          subject: 'Wali Kelas',
          assignedClasses: [activeClass]
        };
        updatedTeachers = [...(teachers || []), newTeacher];
      }
    }

    if (onUpdateTeachers) {
      onUpdateTeachers(updatedTeachers);
    } else if (onUpdateTeacher) {
      const updated = updatedTeachers.find(t => t.homeroomClass === activeClass);
      if (updated) onUpdateTeacher(updated);
    }
  };

  const handleAssignHomeroomTeacher = (teacherId: string) => {
    if (!activeClass || !teachers) return;

    if (!teacherId) {
      handleClearHomeroomTeacher();
      return;
    }

    const newTeachers = teachers.map(t => {
      if (t.homeroomClass === activeClass && t.id !== teacherId) {
        return { ...t, homeroomClass: undefined };
      }
      if (t.id === teacherId) {
        return { ...t, homeroomClass: activeClass };
      }
      return t;
    });

    if (onUpdateTeachers) {
      onUpdateTeachers(newTeachers);
    } else if (onUpdateTeacher) {
      const chosen = newTeachers.find(t => t.id === teacherId);
      if (chosen) onUpdateTeacher(chosen);
    }
  };

  const handleClearHomeroomTeacher = () => {
    if (!activeClass || !teachers) return;
    const newTeachers = teachers.map(t => {
      if (t.homeroomClass === activeClass) {
        return { ...t, homeroomClass: undefined };
      }
      return t;
    });
    if (onUpdateTeachers) {
      onUpdateTeachers(newTeachers);
    } else if (onUpdateTeacher && currentHomeroomTeacher) {
      onUpdateTeacher({ ...currentHomeroomTeacher, homeroomClass: undefined });
    }
  };

  const currentClassStudents = useMemo(() => {
    if (!activeClass) return [];
    return (students || [])
      .filter(s => s.class === activeClass)
      .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'id', { sensitivity: 'base' }));
  }, [students, activeClass]);

  const filteredStudents = useMemo(() => {
    return (students || [])
      .filter(s => {
        const isClassMatch = selectedClass === 'SEMUA' || s.class === selectedClass;
        if (!isClassMatch) return false;

        const name = (s.name || '').toLowerCase();
        const nis = (s.nis || '').toLowerCase();
        const nisn = (s.nisn || '').toLowerCase();
        const term = (searchTerm || '').toLowerCase();

        return name.includes(term) || nis.includes(term) || nisn.includes(term);
      })
      .sort((a, b) => {
        const classCompare = (a.class || '').localeCompare(b.class || '', undefined, { numeric: true, sensitivity: 'base' });
        if (classCompare !== 0) return classCompare;
        return (a.name || '').localeCompare(b.name || '', 'id', { sensitivity: 'base' });
      });
  }, [students, selectedClass, searchTerm]);

  const handleAssignRole = (studentId: string | null, targetRole: string) => {
    if (!activeClass) return;

    const updated = (students || []).map(s => {
      // Clear targetRole from any other student in the same class
      if (s.class === activeClass && s.classRole === targetRole && s.id !== studentId) {
        return { ...s, classRole: undefined };
      }
      if (s.class === activeClass && targetRole === 'Sekretaris' && s.classRole === 'Sekretaris 1' && s.id !== studentId) {
        return { ...s, classRole: undefined };
      }
      if (s.class === activeClass && targetRole === 'Sekretaris 1' && s.classRole === 'Sekretaris' && s.id !== studentId) {
        return { ...s, classRole: undefined };
      }
      if (s.class === activeClass && targetRole === 'Bendahara' && s.classRole === 'Bendahara 1' && s.id !== studentId) {
        return { ...s, classRole: undefined };
      }
      if (s.class === activeClass && targetRole === 'Bendahara 1' && s.classRole === 'Bendahara' && s.id !== studentId) {
        return { ...s, classRole: undefined };
      }
      // If studentId matches, update or clear role
      if (s.id === studentId) {
        return { ...s, classRole: targetRole || undefined };
      }
      return s;
    });

    onUpdate(updated);
  };

  const hasSekretaris2Assigned = useMemo(() => {
    return currentClassStudents.some(s => s.classRole === 'Sekretaris 2');
  }, [currentClassStudents]);

  const hasBendahara2Assigned = useMemo(() => {
    return currentClassStudents.some(s => s.classRole === 'Bendahara 2');
  }, [currentClassStudents]);

  const isSekretaris2Visible = showSekretaris2 || hasSekretaris2Assigned;
  const isBendahara2Visible = showBendahara2 || hasBendahara2Assigned;

  const officerRoles = [
    { 
      id: 'KM', 
      title: 'Ketua Murid (KM)', 
      icon: Crown, 
      colorBg: 'from-amber-500/15 via-amber-500/5 to-slate-900', 
      border: 'border-amber-500/30', 
      badge: 'bg-amber-400 text-slate-950', 
      iconColor: 'text-amber-400' 
    },
    { 
      id: 'Wakil KM', 
      title: 'Wakil KM', 
      icon: ShieldCheck, 
      colorBg: 'from-indigo-500/15 via-indigo-500/5 to-slate-900', 
      border: 'border-indigo-500/30', 
      badge: 'bg-indigo-400 text-slate-950', 
      iconColor: 'text-indigo-400' 
    },
    { 
      id: isSekretaris2Visible ? 'Sekretaris 1' : 'Sekretaris', 
      title: isSekretaris2Visible ? 'Sekretaris 1' : 'Sekretaris', 
      icon: FileText, 
      colorBg: 'from-blue-500/15 via-blue-500/5 to-slate-900', 
      border: 'border-blue-500/30', 
      badge: 'bg-blue-400 text-slate-950', 
      iconColor: 'text-blue-400',
      canAddSecond: !isSekretaris2Visible,
      onAddSecond: () => setShowSekretaris2(true),
      addSecondLabel: '+ Sekretaris 2'
    },
    ...(isSekretaris2Visible ? [{
      id: 'Sekretaris 2', 
      title: 'Sekretaris 2', 
      icon: FileText, 
      colorBg: 'from-blue-500/15 via-blue-500/5 to-slate-900', 
      border: 'border-blue-500/30', 
      badge: 'bg-blue-400 text-slate-950', 
      iconColor: 'text-blue-400',
      isRemovable: true,
      onRemove: () => {
        setShowSekretaris2(false);
        const s2 = currentClassStudents.find(s => s.classRole === 'Sekretaris 2');
        if (s2) handleAssignRole(s2.id, '');
      }
    }] : []),
    { 
      id: isBendahara2Visible ? 'Bendahara 1' : 'Bendahara', 
      title: isBendahara2Visible ? 'Bendahara 1' : 'Bendahara', 
      icon: Wallet, 
      colorBg: 'from-emerald-500/15 via-emerald-500/5 to-slate-900', 
      border: 'border-emerald-500/30', 
      badge: 'bg-emerald-400 text-slate-950', 
      iconColor: 'text-emerald-400',
      canAddSecond: !isBendahara2Visible,
      onAddSecond: () => setShowBendahara2(true),
      addSecondLabel: '+ Bendahara 2'
    },
    ...(isBendahara2Visible ? [{
      id: 'Bendahara 2', 
      title: 'Bendahara 2', 
      icon: Wallet, 
      colorBg: 'from-emerald-500/15 via-emerald-500/5 to-slate-900', 
      border: 'border-emerald-500/30', 
      badge: 'bg-emerald-400 text-slate-950', 
      iconColor: 'text-emerald-400',
      isRemovable: true,
      onRemove: () => {
        setShowBendahara2(false);
        const b2 = currentClassStudents.find(s => s.classRole === 'Bendahara 2');
        if (b2) handleAssignRole(b2.id, '');
      }
    }] : [])
  ];

  const handleExportExcel = () => {
    // Header Data
    const headerData = [
      [schoolSettings.governmentName || ''],
      [schoolSettings.departmentName || ''],
      [schoolSettings.schoolName],
      [schoolSettings.address],
      [],
      ['DAFTAR SISWA'],
      ['Kelas:', selectedClass === 'SEMUA' ? 'Semua Kelas' : `Kelas ${selectedClass}`],
      ['Wali Kelas:', currentHomeroomTeacher ? `${currentHomeroomTeacher.name} (NIP. ${currentHomeroomTeacher.nip || '-'})` : 'Belum Ditentukan'],
      [],
      ['No', 'NISN', 'Nama Lengkap', 'Kelas', 'Jenis Kelamin', 'Jabatan Kelas', 'Catatan Guru']
    ];

    // Student Data
    const studentData = filteredStudents.map((s, idx) => [
      idx + 1,
      s.nisn || s.nis || '',
      s.name,
      s.class,
      s.gender === 'Laki-laki' ? 'L' : 'P',
      s.classRole || 'Anggota',
      s.notes || s.specialNotes || ''
    ]);

    const ws = XLSX.utils.aoa_to_sheet([...headerData, ...studentData]);
    
    // Set column widths
    ws['!cols'] = [{ wch: 5 }, { wch: 15 }, { wch: 30 }, { wch: 10 }, { wch: 15 }, { wch: 18 }, { wch: 35 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Daftar_Siswa");
    XLSX.writeFile(wb, `Daftar_Siswa_${schoolSettings.schoolName.replace(/\s+/g, '_')}.xlsx`);
  };

  const handleOpenModal = (student?: Student) => {
    if (student) {
      setEditingStudent(student);
      setFormData(student);
    } else {
      setEditingStudent(null);
      setFormData({ name: '', nis: '', nisn: '', class: activeClass || sortedClasses[0] || '', gender: 'Laki-laki', status: 'Aktif', classRole: '', notes: '' });
    }
    setIsModalOpen(true);
  };

  const handleSave = () => {
    if (!formData.name || !formData.nis || !formData.class) return;

    if (editingStudent) {
      const updatedList = students.map(s => s.id === editingStudent.id ? { ...s, ...formData } as Student : s);
      onUpdate(updatedList);
    } else {
      const newStudent: Student = {
        ...formData as Student,
        id: `s${Date.now()}`
      };
      onUpdate([...students, newStudent]);
    }
    setIsModalOpen(false);
  };

  // Fungsi Pemicu Modal Konfirmasi
  const triggerDelete = (e: React.MouseEvent, id: string, name: string) => {
    e.preventDefault();
    e.stopPropagation();
    setConfirmDelete({ isOpen: true, id, name });
  };

  const triggerClearAll = (e: React.MouseEvent) => {
    e.preventDefault();
    if (students.length === 0) return;
    setConfirmDelete({ isOpen: true, id: 'ALL', name: `SELURUH Database (${students.length} data)` });
  };

  // Eksekusi Penghapusan Sebenarnya
  const executeDelete = () => {
    if (confirmDelete.id === 'ALL') {
      onUpdate([]);
    } else {
      const updatedList = (students || []).filter(s => s.id !== confirmDelete.id);
      onUpdate(updatedList);
    }
    setConfirmDelete({ isOpen: false, id: '', name: '' });
  };

  const handleDownloadTemplate = () => {
    const templateData = [{ 'NISN': '0112345678', 'Nama': 'Afghan Firmansyah', 'Kelas': '9A', 'Jenis Kelamin': 'L', 'Catatan Guru': 'Siswa berprestasi dan aktif di kelas' }];
    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Daftar_Siswa");
    XLSX.writeFile(wb, "Template_Siswa_SMPN_1_Kawali.xlsx");
  };

  const handleExcelUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" }) as any[];

        const newStudents: Student[] = jsonData.map((row, idx) => {
          const findVal = (needles: string[]) => {
            const key = Object.keys(row).find(k => needles.some(n => k.toLowerCase().replace(/\s/g, '').includes(n.toLowerCase())));
            return key ? row[key] : "";
          };
          const rawNis = findVal(['nis', 'nomorinduk', 'nisn']);
          const rawNisn = findVal(['nisn', 'nis']);
          const rawNama = findVal(['nama', 'name', 'siswa']);
          const rawKelas = findVal(['kelas', 'class', 'rombel']);
          const rawGender = findVal(['jeniskelamin', 'gender', 'jk', 'sex']);
          const rawCatatan = findVal(['catatan', 'note', 'notes', 'catatanguru', 'keterangan']);
          const genderStr = String(rawGender || '').toUpperCase().trim();
          const genderParsed = (genderStr === 'P' || genderStr.startsWith('PEREMPUAN')) ? 'Perempuan' : 'Laki-laki';
          
          return {
            id: `s-xl-${Date.now()}-${idx}`,
            nis: String(rawNisn || rawNis).trim(),
            nisn: String(rawNisn || rawNis).trim(),
            name: String(rawNama).trim(),
            class: String(rawKelas).toUpperCase().trim(),
            gender: genderParsed as 'Laki-laki' | 'Perempuan',
            status: 'Aktif' as 'Aktif' | 'Tidak Aktif',
            notes: rawCatatan ? String(rawCatatan).trim() : undefined,
            specialNotes: rawCatatan ? String(rawCatatan).trim() : undefined
          };
        }).filter(s => s.name && s.nis);

        if (newStudents.length > 0) {
          const uniqueNewStudents = newStudents.filter(s => !(students || []).some(old => (old.nisn === s.nisn || old.nis === s.nis)));
          onUpdate([...(students || []), ...uniqueNewStudents]);
        }
      } catch (err) { console.error(err); }
    };
    reader.readAsArrayBuffer(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="bg-white dark:bg-slate-900 p-6 sm:p-10 rounded-[2.5rem] shadow-sm border border-slate-200 dark:border-slate-800 pb-20">
      <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center mb-10 gap-6">
        <div>
          <h2 className="text-3xl font-black text-slate-800 dark:text-white tracking-tight leading-none mb-3">Kelola Database Siswa</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">{students.length} Siswa Terdaftar di Sistem.</p>
        </div>
        <div className="flex flex-col lg:flex-row flex-wrap items-stretch lg:items-center gap-3 w-full xl:w-auto">
          <div className="relative flex-1 min-w-[200px] xl:w-80">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Cari nama atau NISN..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 rounded-xl outline-none focus:ring-4 focus:ring-indigo-500/10 dark:text-white font-bold transition-all text-sm"
            />
          </div>
          {/* Controls Navigation Kelas (Back, Dropdown Filter, Next) */}
          <div className="flex items-center gap-1 bg-white dark:bg-slate-800 p-1 rounded-xl border-2 border-slate-200 dark:border-slate-700 relative shrink-0">
            <button 
              type="button"
              onClick={handlePrevClass}
              className="flex items-center gap-1 px-3 py-2 bg-slate-50 dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-lg text-xs font-black border border-slate-200 dark:border-slate-700 transition-all active:scale-95"
              title="Ke Kelas Sebelumnya"
            >
              <ChevronLeft size={16} className="text-indigo-600 dark:text-indigo-400" />
              <span className="hidden sm:inline">Sebelumnya</span>
            </button>

            <div className="relative">
              <button 
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                className="flex items-center gap-2 px-3.5 py-2 bg-white dark:bg-slate-900 rounded-lg text-xs font-black text-slate-800 dark:text-white border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-all active:scale-95"
                title="Pilih Filter Kelas"
              >
                <Filter size={15} className="text-indigo-600 dark:text-indigo-400" />
                <span>{selectedClass === 'SEMUA' ? 'Semua Kelas' : `Kelas ${selectedClass}`}</span>
                <ChevronDown size={14} className={`text-slate-400 transition-transform duration-200 ${isFilterOpen ? 'rotate-180' : ''}`} />
              </button>

              {isFilterOpen && (
                <>
                  <div className="fixed inset-0 z-[60]" onClick={() => setIsFilterOpen(false)}></div>
                  <div className="absolute top-full right-0 lg:left-0 mt-2 w-48 bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 z-[70] py-3 max-h-72 overflow-y-auto animate-fade-in-up">
                    <div className="px-4 py-2 mb-1 border-b border-slate-100 dark:border-slate-800">
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Pilih Kelas</p>
                    </div>
                    <button
                      onClick={() => { setSelectedClass('SEMUA'); setIsFilterOpen(false); }}
                      className={`w-full text-left px-4 py-2.5 text-xs font-black transition-colors ${selectedClass === 'SEMUA' ? 'text-indigo-600 bg-indigo-50 dark:bg-indigo-900/20' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'}`}
                    >
                      Semua Kelas
                    </button>
                    {sortedClasses.map(c => (
                      <button
                        key={c}
                        onClick={() => { setSelectedClass(c); setIsFilterOpen(false); }}
                        className={`w-full text-left px-4 py-2.5 text-xs font-black transition-colors ${selectedClass === c ? 'text-indigo-600 bg-indigo-50 dark:bg-indigo-900/20' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'}`}
                      >
                        Kelas {c}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            <button 
              type="button"
              onClick={handleNextClass}
              className="flex items-center gap-1 px-3 py-2 bg-slate-50 dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-lg text-xs font-black border border-slate-200 dark:border-slate-700 transition-all active:scale-95"
              title="Ke Kelas Selanjutnya"
            >
              <span className="hidden sm:inline">Selanjutnya</span>
              <ChevronRight size={16} className="text-indigo-600 dark:text-indigo-400" />
            </button>
          </div>
          
          <div className="grid grid-cols-2 sm:flex items-center gap-2">
            <button 
              type="button" 
              onClick={() => {
                setGroupClass(selectedClass === 'SEMUA' ? (sortedClasses[0] || '9A') : selectedClass);
                setIsGroupModalOpen(true);
              }} 
              className="flex items-center justify-center gap-2 px-5 py-3 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl shadow-lg shadow-purple-500/20 active:scale-95 text-sm transition-all"
            >
              <Users size={18} />
              <span>Buat Kelompok</span>
            </button>

            <button onClick={handleExportExcel} className="flex items-center justify-center gap-2 p-3 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-xl border-2 border-emerald-200 dark:border-emerald-800 hover:bg-emerald-200 transition-colors" title="Export Excel">
              <FileSpreadsheet size={20} /> <span className="sm:hidden text-xs font-bold">Export</span>
            </button>
            <button onClick={handleDownloadTemplate} className="flex items-center justify-center gap-2 p-3 bg-slate-100 dark:bg-slate-800 text-slate-500 rounded-xl border-2 border-slate-200 dark:border-slate-700 hover:bg-slate-200 transition-colors" title="Template Excel">
              <Download size={20} /> <span className="sm:hidden text-xs font-bold">Template</span>
            </button>

            <input type="file" ref={fileInputRef} onChange={handleExcelUpload} accept=".xlsx, .xls" className="hidden" />
            <button type="button" onClick={() => fileInputRef.current?.click()} className="flex items-center justify-center gap-2 px-5 py-3 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-bold rounded-xl hover:bg-slate-200 border-2 border-slate-200 dark:border-slate-700 text-sm transition-colors">
              <FileUp size={18} /> Impor
            </button>

            <button type="button" onClick={() => handleOpenModal()} className="flex items-center justify-center gap-2 px-6 py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 shadow-lg shadow-indigo-500/20 active:scale-95 text-sm transition-all">
              <UserPlus size={18} /> Siswa Baru
            </button>
          </div>
        </div>
      </div>

      {/* STRUKTUR ORGANISASI KELAS (WALI KELAS, KM, WAKIL KM, SEKRETARIS, BENDAHARA) */}
      {showStructure && activeClass && (
        <div className="mb-8 p-6 sm:p-8 bg-slate-900 text-white rounded-[2.5rem] shadow-xl border border-slate-800 relative overflow-hidden animate-fade-in">
          {/* Background Ambient Glow */}
          <div className="absolute top-0 right-0 w-80 h-80 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none"></div>

          {/* Card Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 relative z-10">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Crown size={18} className="text-amber-400" />
                <span className="text-[10px] font-black uppercase text-amber-300 tracking-widest bg-amber-400/10 px-2.5 py-1 rounded-md border border-amber-400/20">
                  Struktur Organisasi
                </span>
              </div>
              <h3 className="text-xl sm:text-2xl font-black tracking-tight text-white">
                Pengurus & Wali Kelas {activeClass}
              </h3>
              <p className="text-xs text-slate-400 font-medium">
                Kelola Guru Wali Kelas, Ketua Murid (KM), Wakil KM, Sekretaris, dan Bendahara untuk Kelas {activeClass}.
              </p>
            </div>

            <div className="flex items-center gap-2">
              {selectedClass === 'SEMUA' && sortedClasses.length > 0 && (
                <div className="flex items-center gap-2 bg-slate-800/80 px-3 py-1.5 rounded-xl border border-slate-700">
                  <span className="text-[10px] font-black uppercase text-slate-400">Pilih Kelas:</span>
                  <select
                    value={activeClass}
                    onChange={(e) => setSelectedClass(e.target.value)}
                    className="bg-transparent text-xs font-black text-amber-400 outline-none cursor-pointer"
                  >
                    {sortedClasses.map(c => <option key={c} value={c} className="bg-slate-900 text-white">Kelas {c}</option>)}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Dynamic Officer Cards Grid Including Wali Kelas */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 relative z-10">
            {/* WALI KELAS CARD */}
            <div className="p-4 rounded-2xl bg-gradient-to-b from-emerald-500/20 via-emerald-500/5 to-slate-900 border border-emerald-500/40 flex flex-col justify-between gap-3 shadow-sm relative group">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded shadow-xs bg-emerald-400 text-slate-950 flex items-center gap-1">
                    <GraduationCap size={12} />
                    WALI KELAS
                  </span>
                  <GraduationCap size={20} className="text-emerald-400" />
                </div>
                <p className="text-xs font-bold text-slate-300">Wali Kelas {activeClass}</p>
              </div>

              <div className="space-y-2">
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">
                    Nama Wali Kelas
                  </label>
                  <div className="flex items-center gap-1.5">
                    <input
                      type="text"
                      value={walasNameInput}
                      onChange={(e) => setWalasNameInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveHomeroomTeacherName(walasNameInput, walasNipInput);
                        }
                      }}
                      onBlur={() => {
                        if (walasNameInput !== (currentHomeroomTeacher?.name || '')) {
                          handleSaveHomeroomTeacherName(walasNameInput, walasNipInput);
                        }
                      }}
                      placeholder="Isi nama Wali Kelas..."
                      className="w-full px-3 py-2 bg-slate-950 text-slate-100 rounded-xl border border-emerald-500/40 text-xs font-bold outline-none focus:border-emerald-400 transition-colors placeholder:text-slate-500"
                    />
                    <button
                      type="button"
                      onClick={() => handleSaveHomeroomTeacherName(walasNameInput, walasNipInput)}
                      className="p-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl transition-all shrink-0 active:scale-95 shadow-xs cursor-pointer"
                      title="Simpan Nama Wali Kelas"
                    >
                      <Check size={14} />
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">
                    NIP (Opsional)
                  </label>
                  <div className="flex items-center gap-1.5">
                    <input
                      type="text"
                      value={walasNipInput}
                      onChange={(e) => setWalasNipInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveHomeroomTeacherName(walasNameInput, walasNipInput);
                        }
                      }}
                      onBlur={() => {
                        if (walasNipInput !== (currentHomeroomTeacher?.nip || '')) {
                          handleSaveHomeroomTeacherName(walasNameInput, walasNipInput);
                        }
                      }}
                      placeholder="NIP Wali Kelas..."
                      className="w-full px-3 py-1.5 bg-slate-950/70 text-slate-300 rounded-lg border border-slate-700 text-[11px] font-medium outline-none focus:border-emerald-400 transition-colors placeholder:text-slate-600 font-mono"
                    />
                    {currentHomeroomTeacher && (
                      <button
                        type="button"
                        onClick={() => {
                          setWalasNameInput('');
                          setWalasNipInput('');
                          handleClearHomeroomTeacher();
                        }}
                        className="p-1.5 bg-slate-900 hover:bg-rose-500/20 text-slate-400 hover:text-rose-400 rounded-lg transition-colors shrink-0 cursor-pointer"
                        title="Kosongkan Wali Kelas"
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {officerRoles.map((role) => {
              const assignedStudent = currentClassStudents.find(s => 
                s.classRole === role.id || 
                (role.id === 'Sekretaris' && (s.classRole === 'Sekretaris 1' || s.classRole === 'Sekretaris')) ||
                (role.id === 'Sekretaris 1' && (s.classRole === 'Sekretaris 1' || s.classRole === 'Sekretaris')) ||
                (role.id === 'Bendahara' && (s.classRole === 'Bendahara 1' || s.classRole === 'Bendahara')) ||
                (role.id === 'Bendahara 1' && (s.classRole === 'Bendahara 1' || s.classRole === 'Bendahara'))
              );
              const RoleIcon = role.icon;

              return (
                <div key={role.id} className={`p-4 rounded-2xl bg-gradient-to-b ${role.colorBg} border ${role.border} flex flex-col justify-between gap-3 shadow-sm relative group`}>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded shadow-xs ${role.badge}`}>
                        {role.id}
                      </span>
                      <div className="flex items-center gap-1.5">
                        {role.canAddSecond && (
                          <button
                            type="button"
                            onClick={role.onAddSecond}
                            className="px-2 py-0.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-[10px] font-black flex items-center gap-1 transition-colors border border-white/15"
                            title={role.addSecondLabel}
                          >
                            <Plus size={11} />
                            <span>Tambah ke-2</span>
                          </button>
                        )}
                        {role.isRemovable && (
                          <button
                            type="button"
                            onClick={role.onRemove}
                            className="p-1 hover:bg-rose-500/20 text-slate-400 hover:text-rose-400 rounded-lg transition-colors"
                            title="Hapus slot pengurus ke-2"
                          >
                            <X size={14} />
                          </button>
                        )}
                        <RoleIcon size={18} className={role.iconColor} />
                      </div>
                    </div>
                    <p className="text-xs font-bold text-slate-300">{role.title}</p>
                  </div>

                  <div>
                    {assignedStudent ? (
                      <div className="bg-slate-950/70 p-3 rounded-xl border border-white/10 flex items-center justify-between gap-2 mb-2">
                        <div className="min-w-0">
                          <p className="text-xs font-black text-white truncate" title={assignedStudent.name}>
                            {assignedStudent.name}
                          </p>
                          <p className="text-[10px] text-slate-400 font-mono">
                            NISN: {assignedStudent.nisn || assignedStudent.nis || '-'}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleAssignRole(assignedStudent.id, '')}
                          className="p-1 hover:bg-rose-500/20 text-rose-400 rounded-lg transition-colors shrink-0"
                          title="Batalkan Jabatan"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <div className="text-[10px] font-bold text-slate-500 italic mb-2 py-1">
                        Belum ada siswa ditunjuk
                      </div>
                    )}

                    <select
                      value={assignedStudent?.id || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val) {
                          handleAssignRole(val, role.id);
                        } else if (assignedStudent) {
                          handleAssignRole(assignedStudent.id, '');
                        }
                      }}
                      className="w-full p-2 bg-slate-950 text-slate-200 rounded-xl border border-slate-700 text-xs font-bold outline-none cursor-pointer focus:border-indigo-400 transition-colors"
                    >
                      <option value="" className="bg-slate-900 text-slate-400">
                        {assignedStudent ? '-- Ganti Siswa --' : '-- Pilih Siswa --'}
                      </option>
                      {currentClassStudents.map(s => (
                        <option key={s.id} value={s.id} className="bg-slate-900 text-white">
                          {s.name} {s.classRole ? `(${s.classRole})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Informative Class Bar with Next/Back Navigation */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 p-4 bg-slate-50 dark:bg-slate-800/60 rounded-2xl border border-slate-200 dark:border-slate-700/80">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-indigo-500 animate-pulse"></span>
            <span className="text-xs font-black text-slate-700 dark:text-slate-200">
              Daftar Siswa: <strong className="text-indigo-600 dark:text-indigo-400">{selectedClass === 'SEMUA' ? 'Semua Kelas' : `Kelas ${selectedClass}`}</strong> ({filteredStudents.length} Siswa)
            </span>
          </div>

          {activeClass && (
            <div className="flex items-center gap-1.5 px-3 py-1 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 rounded-xl border border-emerald-200/80 dark:border-emerald-800/80 text-xs font-bold shadow-2xs">
              <GraduationCap size={15} className="text-emerald-600 dark:text-emerald-400 shrink-0" />
              <span className="text-slate-500 dark:text-slate-400 text-[11px] font-semibold">Wali Kelas:</span>
              <strong className="font-black text-emerald-700 dark:text-emerald-300">
                {currentHomeroomTeacher ? currentHomeroomTeacher.name : 'Belum Ditentukan'}
              </strong>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handlePrevClass}
            className="flex items-center gap-1.5 px-3.5 py-1.5 bg-white dark:bg-slate-900 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 text-slate-700 dark:text-slate-200 font-black text-xs rounded-xl border border-slate-200 dark:border-slate-700 transition-all active:scale-95 shadow-2xs"
            title="Ke Kelas Sebelumnya"
          >
            <ChevronLeft size={16} className="text-indigo-600 dark:text-indigo-400" />
            <span>Sebelumnya</span>
          </button>
          <span className="text-[10px] font-black text-slate-400 px-1">
            {selectedClass === 'SEMUA' ? 'Semua' : `${sortedClasses.indexOf(selectedClass) + 1} / ${sortedClasses.length}`}
          </span>
          <button
            type="button"
            onClick={handleNextClass}
            className="flex items-center gap-1.5 px-3.5 py-1.5 bg-white dark:bg-slate-900 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 text-slate-700 dark:text-slate-200 font-black text-xs rounded-xl border border-slate-200 dark:border-slate-700 transition-all active:scale-95 shadow-2xs"
            title="Ke Kelas Selanjutnya"
          >
            <span>Selanjutnya</span>
            <ChevronRight size={16} className="text-indigo-600 dark:text-indigo-400" />
          </button>
        </div>
      </div>

      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full text-base text-left border-collapse border border-slate-200 dark:border-slate-800">
          <thead>
            <tr className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 uppercase text-xs font-black tracking-[0.2em]">
              <th className="px-4 py-4 border border-slate-200 dark:border-slate-800">No</th>
              <th className="px-4 py-4 border border-slate-200 dark:border-slate-800">Nama Lengkap</th>
              <th className="px-4 py-4 border border-slate-200 dark:border-slate-800">NISN</th>
              <th className="px-4 py-4 border border-slate-200 dark:border-slate-800">Kelas</th>
              <th className="px-4 py-4 border border-slate-200 dark:border-slate-800">JK</th>
              <th className="px-4 py-4 border border-slate-200 dark:border-slate-800 min-w-[200px]">Catatan Guru</th>
              <th className="px-4 py-4 border border-slate-200 dark:border-slate-800 text-center">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {filteredStudents.map((student, idx) => (
              <tr key={student.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                <td className="px-4 py-4 border border-slate-200 dark:border-slate-800 font-bold text-slate-500 dark:text-slate-400">{idx + 1}</td>
                <td 
                  className={`px-4 py-4 border border-slate-200 dark:border-slate-800 font-black ${student.gender === 'Laki-laki' ? 'text-blue-600 dark:text-blue-400' : 'text-rose-600 dark:text-rose-400'} ${onStudentClick ? 'cursor-pointer hover:underline hover:text-indigo-600 transition-colors' : ''}`}
                  onClick={(e) => {
                    if (onStudentClick) {
                      e.preventDefault();
                      e.stopPropagation();
                      onStudentClick(student);
                    }
                  }}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span>{student.name}</span>
                    {student.classRole && (
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider border ${
                        student.classRole === 'KM' 
                          ? 'bg-amber-100 dark:bg-amber-950/50 text-amber-800 dark:text-amber-300 border-amber-300 dark:border-amber-800'
                          : student.classRole === 'Wakil KM'
                          ? 'bg-indigo-100 dark:bg-indigo-950/50 text-indigo-800 dark:text-indigo-300 border-indigo-300 dark:border-indigo-800'
                          : student.classRole === 'Sekretaris'
                          ? 'bg-blue-100 dark:bg-blue-950/50 text-blue-800 dark:text-blue-300 border-blue-300 dark:border-blue-800'
                          : 'bg-emerald-100 dark:bg-emerald-950/50 text-emerald-800 dark:text-emerald-300 border-emerald-300 dark:border-emerald-800'
                      }`}>
                        {student.classRole === 'KM' && <Crown size={10} className="shrink-0" />}
                        {student.classRole === 'Wakil KM' && <ShieldCheck size={10} className="shrink-0" />}
                        {student.classRole === 'Sekretaris' && <FileText size={10} className="shrink-0" />}
                        {student.classRole === 'Bendahara' && <Wallet size={10} className="shrink-0" />}
                        {student.classRole}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-4 border border-slate-200 dark:border-slate-800 font-bold text-slate-500 dark:text-slate-400">{student.nisn || student.nis || '-'}</td>
                <td className="px-4 py-4 border border-slate-200 dark:border-slate-800">
                    <select 
                        value={student.class}
                        onChange={(e) => {
                            const updatedList = students.map(s => s.id === student.id ? { ...s, class: e.target.value } : s);
                            onUpdate(updatedList);
                        }}
                        className="px-3 py-2 rounded-lg font-black text-xs bg-indigo-600 text-white border-none outline-none cursor-pointer"
                    >
                        {sortedClasses.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                </td>
                <td className="px-4 py-4 border border-slate-200 dark:border-slate-800"><span className="font-black text-xs uppercase">{student.gender === 'Laki-laki' ? 'L' : 'P'}</span></td>
                <td className="px-4 py-4 border border-slate-200 dark:border-slate-800">
                  {student.notes || student.specialNotes ? (
                    <div 
                      onClick={(e) => handleOpenNoteModal(e, student)}
                      className="group/note flex items-start gap-1.5 p-2 bg-amber-50/80 dark:bg-amber-950/30 hover:bg-amber-100 dark:hover:bg-amber-900/50 rounded-xl border border-amber-200/80 dark:border-amber-800/60 cursor-pointer transition-all max-w-[280px]"
                      title="Klik untuk melihat/mengubah catatan guru"
                    >
                      <StickyNote size={14} className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                      <span className="text-xs font-medium text-amber-900 dark:text-amber-200 line-clamp-2 leading-tight">
                        {student.notes || student.specialNotes}
                      </span>
                      <Edit2 size={12} className="text-amber-500 opacity-0 group-hover/note:opacity-100 shrink-0 ml-auto transition-opacity" />
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={(e) => handleOpenNoteModal(e, student)}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold text-slate-400 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/40 border border-dashed border-slate-200 dark:border-slate-700 transition-all cursor-pointer"
                      title="Tambah catatan guru untuk siswa ini"
                    >
                      <Plus size={12} />
                      <span>Catatan</span>
                    </button>
                  )}
                </td>
                <td className="px-4 py-4 border border-slate-200 dark:border-slate-800 text-center">
                  <div className="flex justify-center gap-2">
                    <button type="button" onClick={() => handleOpenModal(student)} className="p-2.5 text-indigo-500 hover:bg-indigo-600 hover:text-white rounded-lg border border-indigo-100 dark:border-slate-700 transition-colors" title="Edit Biodata Siswa"><Edit2 size={18}/></button>
                    <button type="button" onClick={(e) => triggerDelete(e, student.id, student.name)} className="p-2.5 text-rose-500 hover:bg-rose-600 hover:text-white rounded-lg border border-rose-100 dark:border-slate-700 transition-colors" title="Hapus"><Trash size={18}/></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile View: Card Layout */}
      <div className="md:hidden space-y-4">
        {filteredStudents.map((student, idx) => (
          <div key={student.id} className="bg-white dark:bg-slate-800 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-4">
            <div className="flex justify-between items-start">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-slate-100 dark:bg-slate-700 rounded-lg flex items-center justify-center text-xs font-black text-slate-500">
                  {idx + 1}
                </div>
                <div 
                  className={onStudentClick ? 'cursor-pointer group/name' : ''}
                  onClick={(e) => {
                    if (onStudentClick) {
                      e.preventDefault();
                      e.stopPropagation();
                      onStudentClick(student);
                    }
                  }}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className={`font-black text-base transition-colors ${student.gender === 'Laki-laki' ? 'text-blue-600 dark:text-blue-400' : 'text-rose-600 dark:text-rose-400'} ${onStudentClick ? 'group-hover/name:text-indigo-600 group-hover/name:underline' : ''}`}>
                      {student.name}
                    </h4>
                    {student.classRole && (
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider border ${
                        student.classRole === 'KM' 
                          ? 'bg-amber-100 dark:bg-amber-950/50 text-amber-800 dark:text-amber-300 border-amber-300 dark:border-amber-800'
                          : student.classRole === 'Wakil KM'
                          ? 'bg-indigo-100 dark:bg-indigo-950/50 text-indigo-800 dark:text-indigo-300 border-indigo-300 dark:border-indigo-800'
                          : student.classRole === 'Sekretaris'
                          ? 'bg-blue-100 dark:bg-blue-950/50 text-blue-800 dark:text-blue-300 border-blue-300 dark:border-blue-800'
                          : 'bg-emerald-100 dark:bg-emerald-950/50 text-emerald-800 dark:text-emerald-300 border-emerald-300 dark:border-emerald-800'
                      }`}>
                        {student.classRole === 'KM' && <Crown size={10} className="shrink-0" />}
                        {student.classRole === 'Wakil KM' && <ShieldCheck size={10} className="shrink-0" />}
                        {student.classRole === 'Sekretaris' && <FileText size={10} className="shrink-0" />}
                        {student.classRole === 'Bendahara' && <Wallet size={10} className="shrink-0" />}
                        {student.classRole}
                      </span>
                    )}
                  </div>
                  <p className="text-xs font-bold text-slate-400 tracking-wider">NISN: {student.nisn || student.nis || '-'}</p>
                </div>
              </div>
              <span className={`px-2 py-1 rounded-lg text-[10px] font-black uppercase ${student.gender === 'Laki-laki' ? 'bg-blue-100 text-blue-600' : 'bg-rose-100 text-rose-600'}`}>
                {student.gender === 'Laki-laki' ? 'L' : 'P'}
              </span>
            </div>

            {/* Catatan Guru Section */}
            <div className="pt-2 border-t border-slate-100 dark:border-slate-700/60">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1">
                  <StickyNote size={12} className="text-amber-500" />
                  Catatan Guru:
                </span>
                <button
                  type="button"
                  onClick={(e) => handleOpenNoteModal(e, student)}
                  className="text-[11px] font-bold text-amber-600 dark:text-amber-400 hover:underline flex items-center gap-1 cursor-pointer"
                >
                  <Edit2 size={11} />
                  <span>{student.notes || student.specialNotes ? 'Ubah' : '+ Tambah'}</span>
                </button>
              </div>
              {student.notes || student.specialNotes ? (
                <div 
                  onClick={(e) => handleOpenNoteModal(e, student)}
                  className="p-2.5 bg-amber-50/80 dark:bg-amber-950/30 rounded-xl border border-amber-200/80 dark:border-amber-800/60 cursor-pointer"
                >
                  <p className="text-xs font-medium text-amber-900 dark:text-amber-200 leading-relaxed">
                    {student.notes || student.specialNotes}
                  </p>
                </div>
              ) : (
                <p className="text-xs italic text-slate-400 dark:text-slate-500">
                  Belum ada catatan khusus untuk siswa ini.
                </p>
              )}
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-700">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Kelas:</span>
                <select 
                  value={student.class}
                  onChange={(e) => {
                    const updatedList = students.map(s => s.id === student.id ? { ...s, class: e.target.value } : s);
                    onUpdate(updatedList);
                  }}
                  className="px-3 py-1.5 rounded-xl font-black text-[10px] bg-indigo-600 text-white border-none outline-none cursor-pointer"
                >
                  {sortedClasses.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              
              <div className="flex gap-2">
                <button type="button" onClick={() => handleOpenModal(student)} className="p-2.5 text-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 rounded-xl border border-indigo-100 dark:border-indigo-900/30 transition-colors" title="Edit">
                  <Edit2 size={16}/>
                </button>
                <button type="button" onClick={(e) => triggerDelete(e, student.id, student.name)} className="p-2.5 text-rose-500 bg-rose-50 dark:bg-rose-900/20 rounded-xl border border-rose-100 dark:border-rose-900/30 transition-colors" title="Hapus">
                  <Trash size={16}/>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredStudents.length === 0 && (
          <div className="p-20 text-center bg-slate-50/50 dark:bg-slate-800/30 rounded-[2rem] border-2 border-dashed border-slate-200 dark:border-slate-800">
              <AlertCircle className="mx-auto mb-4 text-slate-300" size={48} />
              <p className="text-slate-400 font-bold italic text-lg">Tidak ada data siswa ditemukan.</p>
              <p className="text-slate-400 text-sm mt-1">Gunakan tombol "Siswa Baru" atau "Impor Excel" untuk menambah data.</p>
          </div>
      )}

      {/* MODAL INPUT/EDIT SISWA */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
           <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm" onClick={() => setIsModalOpen(false)}></div>
           <div className="bg-white dark:bg-slate-900 w-full max-w-md rounded-[2.5rem] shadow-2xl relative overflow-hidden animate-fade-in-up border border-slate-200 dark:border-slate-800 max-h-[90vh] flex flex-col">
              <div className="bg-indigo-600 p-8 text-white shrink-0"><h3 className="text-2xl font-black">{editingStudent ? 'Edit Biodata' : 'Input Siswa'}</h3></div>
              <div className="p-8 space-y-4 overflow-y-auto">
                 <div><label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Nama Lengkap</label><input type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full px-5 py-3.5 bg-slate-50 dark:bg-slate-800 border rounded-2xl outline-none font-bold dark:text-white focus:ring-4 focus:ring-indigo-500/10 border-slate-200 dark:border-slate-700"/></div>
                 <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">NISN</label>
                    <input type="text" value={formData.nisn || formData.nis || ''} onChange={e => setFormData({...formData, nisn: e.target.value, nis: e.target.value})} className="w-full px-5 py-3.5 bg-slate-50 dark:bg-slate-800 border rounded-2xl outline-none font-bold dark:text-white focus:ring-4 focus:ring-indigo-500/10 border-slate-200 dark:border-slate-700"/>
                 </div>
                 <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Rombel / Kelas</label>
                    <select value={formData.class} onChange={e => setFormData({...formData, class: e.target.value})} className="w-full px-5 py-3.5 bg-slate-50 dark:bg-slate-800 border rounded-2xl outline-none font-bold dark:text-white border-slate-200 dark:border-slate-700">
                      {sortedClasses.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    {formData.class && (
                      <div className="mt-2 p-2.5 bg-slate-50 dark:bg-slate-800/80 rounded-xl border border-slate-200 dark:border-slate-700 flex items-center justify-between">
                        <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                          <GraduationCap size={14} className="text-emerald-500" />
                          Wali Kelas {formData.class}:
                        </span>
                        <span className="text-xs font-black text-emerald-600 dark:text-emerald-400">
                          {teachers?.find(t => t.homeroomClass === formData.class)?.name || 'Belum Ditentukan'}
                        </span>
                      </div>
                    )}
                 </div>
                 <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Jabatan Struktur Kelas</label>
                    <select value={formData.classRole || ''} onChange={e => setFormData({...formData, classRole: e.target.value})} className="w-full px-5 py-3.5 bg-slate-50 dark:bg-slate-800 border rounded-2xl outline-none font-bold dark:text-white border-slate-200 dark:border-slate-700">
                      <option value="">-- Bukan Pengurus (Anggota) --</option>
                      <option value="KM">Ketua Murid (KM)</option>
                      <option value="Wakil KM">Wakil KM</option>
                      <option value="Sekretaris 1">Sekretaris 1</option>
                      <option value="Sekretaris 2">Sekretaris 2</option>
                      <option value="Bendahara 1">Bendahara 1</option>
                      <option value="Bendahara 2">Bendahara 2</option>
                      <option value="Sekretaris">Sekretaris (Umum)</option>
                      <option value="Bendahara">Bendahara (Umum)</option>
                    </select>
                 </div>
                 <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 flex items-center gap-1.5">
                      <StickyNote size={14} className="text-amber-500" />
                      Catatan Guru / Keterangan Khusus
                    </label>
                    <textarea 
                      rows={3} 
                      placeholder="Catatan perkembangan, sikap, potensi, atau bimbingan siswa..."
                      value={formData.notes || formData.specialNotes || ''} 
                      onChange={e => setFormData({...formData, notes: e.target.value, specialNotes: e.target.value})} 
                      className="w-full px-5 py-3.5 bg-slate-50 dark:bg-slate-800 border rounded-2xl outline-none font-medium dark:text-white focus:ring-4 focus:ring-indigo-500/10 border-slate-200 dark:border-slate-700 text-sm leading-relaxed"
                    />
                 </div>
                 <button type="button" onClick={handleSave} className="w-full py-5 bg-indigo-600 text-white font-black rounded-3xl hover:bg-indigo-700 shadow-xl shadow-indigo-500/20 active:scale-95 flex items-center justify-center gap-3 transition-all uppercase tracking-widest text-sm">SIMPAN PERUBAHAN</button>
              </div>
           </div>
        </div>
      )}

      {/* CUSTOM MODAL KONFIRMASI HAPUS */}
      {confirmDelete.isOpen && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-md animate-fade-in" onClick={() => setConfirmDelete({...confirmDelete, isOpen: false})}></div>
          <div className="bg-white dark:bg-slate-900 w-full max-w-sm rounded-[2.5rem] shadow-2xl relative overflow-hidden animate-fade-in-up border border-rose-500/20 text-center">
            <div className="p-10">
              <div className="w-20 h-20 bg-rose-50 dark:bg-rose-500/10 text-rose-500 rounded-3xl flex items-center justify-center mx-auto mb-6">
                <AlertTriangle size={40} />
              </div>
              <h3 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight mb-2">Konfirmasi Hapus</h3>
              <p className="text-slate-500 dark:text-slate-400 text-sm font-medium mb-8">
                Apakah Anda yakin ingin menghapus <span className="text-rose-500 font-black">{confirmDelete.name}</span>? 
                Data yang dihapus tidak dapat dikembalikan.
              </p>
              <div className="flex flex-col gap-3">
                <button 
                  type="button" 
                  onClick={executeDelete} 
                  className="w-full py-4 bg-rose-500 text-white font-black rounded-2xl hover:bg-rose-600 active:scale-95 transition-all shadow-xl shadow-rose-500/20 uppercase tracking-widest text-xs"
                >
                  Ya, Hapus Sekarang
                </button>
                <button 
                  type="button" 
                  onClick={() => setConfirmDelete({...confirmDelete, isOpen: false})} 
                  className="w-full py-4 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-black rounded-2xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-all uppercase tracking-widest text-xs"
                >
                  Batalkan
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL BUAT KELOMPOK BELAJAR ADIL GENDER */}
      {isGroupModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
          <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-md animate-fade-in" onClick={() => setIsGroupModalOpen(false)}></div>
          <div className="bg-white dark:bg-slate-900 w-full max-w-5xl max-h-[90vh] rounded-[2.5rem] shadow-2xl relative overflow-hidden animate-fade-in-up border border-purple-500/20 flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 p-6 sm:p-8 text-white relative shrink-0">
              <button 
                onClick={() => setIsGroupModalOpen(false)}
                className="absolute top-6 right-6 p-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors"
                title="Tutup"
              >
                <X size={20} />
              </button>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2.5 bg-white/15 rounded-2xl border border-white/20">
                  <Sparkles size={24} className="text-yellow-300" />
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-black tracking-tight">Pembagian Kelompok Belajar (Adil Gender)</h3>
                  <p className="text-xs sm:text-sm text-purple-100 font-medium">
                    Sistem membagi siswa Laki-laki dan Perempuan secara proporsional & adil ke dalam kelompok.
                  </p>
                </div>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 sm:p-8 overflow-y-auto space-y-6 custom-scrollbar flex-1">
              {/* Controls Bar */}
              <div className="bg-slate-50 dark:bg-slate-800/60 p-5 rounded-2xl border border-slate-200 dark:border-slate-700 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                <div className="flex flex-wrap items-center gap-4 w-full lg:w-auto">
                  {/* Choose Class */}
                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Pilih Kelas</label>
                    <select
                      value={groupClass}
                      onChange={(e) => setGroupClass(e.target.value)}
                      className="px-4 py-2.5 bg-white dark:bg-slate-900 border-2 border-slate-200 dark:border-slate-700 rounded-xl text-xs font-black dark:text-white outline-none cursor-pointer focus:border-purple-500"
                    >
                      {sortedClasses.map(c => (
                        <option key={c} value={c}>Kelas {c}</option>
                      ))}
                    </select>
                  </div>

                  {/* Set Number of Groups */}
                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Jumlah Kelompok</label>
                    <div className="flex items-center gap-1.5 bg-white dark:bg-slate-900 border-2 border-slate-200 dark:border-slate-700 rounded-xl p-1">
                      <button
                        type="button"
                        onClick={() => setGroupCount(prev => Math.max(2, prev - 1))}
                        className="w-8 h-8 flex items-center justify-center bg-slate-100 dark:bg-slate-800 hover:bg-purple-100 dark:hover:bg-purple-900/30 text-slate-700 dark:text-slate-200 rounded-lg font-black transition-colors"
                      >
                        -
                      </button>
                      <span className="w-10 text-center font-black text-sm text-purple-600 dark:text-purple-400">{groupCount}</span>
                      <button
                        type="button"
                        onClick={() => setGroupCount(prev => Math.min(Math.max(2, groupStudents.length), prev + 1))}
                        className="w-8 h-8 flex items-center justify-center bg-slate-100 dark:bg-slate-800 hover:bg-purple-100 dark:hover:bg-purple-900/30 text-slate-700 dark:text-slate-200 rounded-lg font-black transition-colors"
                      >
                        +
                      </button>
                    </div>
                  </div>

                  {/* Stats Pill */}
                  <div className="flex items-center gap-2 pt-4 lg:pt-0">
                    <span className="px-3 py-1.5 bg-indigo-50 dark:bg-indigo-950/50 border border-indigo-200 dark:border-indigo-800/50 rounded-xl text-xs font-black text-indigo-600 dark:text-indigo-300">
                      Total: {groupStudents.length} Siswa
                    </span>
                    <span className="px-3 py-1.5 bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-800/50 rounded-xl text-xs font-black text-blue-600 dark:text-blue-300">
                      👦 {maleStudents.length} L
                    </span>
                    <span className="px-3 py-1.5 bg-pink-50 dark:bg-pink-950/50 border border-pink-200 dark:border-pink-800/50 rounded-xl text-xs font-black text-pink-600 dark:text-pink-300">
                      👧 {femaleStudents.length} P
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2 w-full lg:w-auto justify-end">
                  <button
                    type="button"
                    onClick={() => setGroupSeed(prev => prev + 1)}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl text-xs transition-all shadow-md shadow-amber-500/20 active:scale-95"
                    title="Acak Ulang Anggota Kelompok"
                  >
                    <Shuffle size={16} />
                    <span>Acak Ulang</span>
                  </button>
                </div>
              </div>

              {/* Actions Bar for Generated Groups */}
              {generatedGroups.length > 0 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-purple-50 dark:bg-purple-950/30 p-4 rounded-2xl border border-purple-200 dark:border-purple-800/40">
                  <div className="text-xs font-bold text-purple-900 dark:text-purple-200">
                    💡 <span className="font-black">Selesai Dibagi:</span> Pembagian jumlah siswa Laki-laki dan Perempuan sudah adil.
                  </div>
                  <div className="flex items-center gap-2 w-full sm:w-auto shrink-0">
                    <button
                      type="button"
                      onClick={handleCopyGroupText}
                      className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-4 py-2.5 bg-white dark:bg-slate-800 border-2 border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-900/50 font-black rounded-xl text-xs transition-all active:scale-95"
                    >
                      {copiedToast ? <CheckCircle2 size={16} className="text-emerald-500" /> : <Copy size={16} />}
                      <span>{copiedToast ? 'Teks Disalin!' : 'Salin Teks (WA)'}</span>
                    </button>
                    <button
                      type="button"
                      onClick={handleExportGroupExcel}
                      className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-black rounded-xl text-xs transition-all shadow-md shadow-emerald-600/20 active:scale-95"
                    >
                      <FileSpreadsheet size={16} />
                      <span>Export Excel</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Group Cards Grid */}
              {groupStudents.length === 0 ? (
                <div className="py-16 text-center bg-slate-50 dark:bg-slate-800/40 rounded-3xl border-2 border-dashed border-slate-200 dark:border-slate-700">
                  <Users className="mx-auto mb-3 text-slate-300 dark:text-slate-600" size={48} />
                  <p className="text-slate-500 dark:text-slate-400 font-bold text-sm">Tidak ada data siswa aktif di Kelas {groupClass}.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {generatedGroups.map((g) => (
                    <div key={g.id} className="bg-white dark:bg-slate-800/90 rounded-2xl border-2 border-slate-200 dark:border-slate-700 shadow-sm hover:border-purple-300 dark:hover:border-purple-600 transition-all flex flex-col justify-between overflow-hidden">
                      <div>
                        {/* Card Header */}
                        <div className="p-4 bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span className="w-7 h-7 rounded-xl bg-purple-600 text-white font-black text-xs flex items-center justify-center shadow-xs">
                              {g.id}
                            </span>
                            <h4 className="font-black text-slate-800 dark:text-white text-sm">{g.name}</h4>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 rounded-md text-[10px] font-black" title="Laki-laki">
                              {g.males.length} L
                            </span>
                            <span className="px-2 py-0.5 bg-pink-100 dark:bg-pink-950/60 text-pink-700 dark:text-pink-300 rounded-md text-[10px] font-black" title="Perempuan">
                              {g.females.length} P
                            </span>
                            <span className="px-2 py-0.5 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-md text-[10px] font-black" title="Total Siswa">
                              {g.members.length} Siswa
                            </span>
                          </div>
                        </div>

                        {/* Members List */}
                        <div className="p-4 space-y-2 max-h-72 overflow-y-auto custom-scrollbar">
                          {g.members.map((m, idx) => {
                            const isFemale = isFemaleStudent(m);
                            return (
                              <div key={m.id} className="flex items-center justify-between p-2 rounded-xl bg-slate-50/60 dark:bg-slate-900/40 border border-slate-100 dark:border-slate-800/80 hover:bg-purple-50/50 dark:hover:bg-purple-950/20 transition-colors">
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className="text-[10px] font-black text-slate-400 w-5 shrink-0 text-right">
                                    {idx + 1}.
                                  </span>
                                  <span className={`text-xs font-black truncate ${isFemale ? 'text-pink-600 dark:text-pink-400' : 'text-blue-600 dark:text-blue-400'}`}>
                                    {m.name}
                                  </span>
                                </div>
                                <span className={`shrink-0 px-2 py-0.5 rounded-full text-[9px] font-black uppercase ${
                                  isFemale 
                                    ? 'bg-pink-100 dark:bg-pink-950/50 text-pink-600 dark:text-pink-300' 
                                    : 'bg-blue-100 dark:bg-blue-950/50 text-blue-600 dark:text-blue-300'
                                }`}>
                                  {isFemale ? 'Perempuan' : 'Laki-laki'}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-5 bg-slate-50 dark:bg-slate-800/80 border-t border-slate-200 dark:border-slate-700 flex justify-end shrink-0">
              <button
                type="button"
                onClick={() => setIsGroupModalOpen(false)}
                className="px-6 py-2.5 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 font-black rounded-xl text-xs hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DEDICATED MODAL CATATAN GURU */}
      {noteModalStudent && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm animate-fade-in" onClick={() => setNoteModalStudent(null)}></div>
          <div className="bg-white dark:bg-slate-900 w-full max-w-lg rounded-[2.5rem] shadow-2xl relative overflow-hidden animate-fade-in-up border border-amber-500/30 flex flex-col">
            <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600 p-6 sm:p-7 text-white flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-white/20 flex items-center justify-center backdrop-blur-xs">
                  <StickyNote size={20} className="text-white" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-black leading-tight">Catatan Guru</h3>
                  <p className="text-xs text-white/80 font-medium">
                    {noteModalStudent.name} • Kelas {noteModalStudent.class}
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setNoteModalStudent(null)}
                className="p-2 hover:bg-white/20 rounded-xl transition-all cursor-pointer text-white"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 sm:p-8 space-y-5">
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">
                  Isi Catatan Perkembangan / Sikap / Bimbingan Siswa
                </label>
                <textarea
                  rows={5}
                  autoFocus
                  placeholder="Tuliskan catatan guru untuk siswa ini (misal: potensi belajar, keaktifan, catatan bimbingan, kendala, atau prestasi)..."
                  value={noteInput}
                  onChange={(e) => setNoteInput(e.target.value)}
                  className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none font-medium text-sm text-slate-800 dark:text-white focus:ring-4 focus:ring-amber-500/20 focus:border-amber-500 transition-all leading-relaxed"
                />
              </div>

              {/* Quick Suggestion Chips */}
              <div>
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">
                  Pilihan Frasa Cepat:
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    'Sangat aktif dan berprestasi di kelas',
                    'Perlu pendampingan pemahaman konsep',
                    'Memiliki kepemimpinan tinggi',
                    'Rajin mengumpulkan tugas tepat waktu',
                    'Perlu motivasi kedisiplinan dan kehadiran',
                    'Berbakat dalam diskusi & kerja tim'
                  ].map((chip, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setNoteInput(prev => prev ? `${prev} ${chip}.` : `${chip}.`)}
                      className="text-[11px] font-bold px-2.5 py-1 rounded-lg bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border border-amber-200/80 dark:border-amber-800/60 hover:bg-amber-100 dark:hover:bg-amber-900/60 transition-colors cursor-pointer"
                    >
                      + {chip}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleSaveNote}
                  className="flex-1 py-4 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-black rounded-2xl shadow-lg shadow-amber-500/20 active:scale-95 transition-all text-xs uppercase tracking-widest flex items-center justify-center gap-2 cursor-pointer"
                >
                  <Check size={16} />
                  <span>Simpan Catatan Guru</span>
                </button>
                <button
                  type="button"
                  onClick={() => setNoteModalStudent(null)}
                  className="px-6 py-4 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-black rounded-2xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-all text-xs uppercase tracking-widest cursor-pointer"
                >
                  Batal
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Floating Toast Notification */}
      {noteSavedToast && (
        <div className="fixed bottom-6 right-6 z-[130] bg-emerald-600 text-white px-5 py-3 rounded-2xl shadow-xl flex items-center gap-2.5 font-bold text-xs animate-fade-in-up">
          <CheckCircle2 size={16} />
          <span>Catatan guru berhasil disimpan!</span>
        </div>
      )}
    </div>
  );
};

export default StudentsView;
